// src/pages/tender/EngineerRaBillGenerator.js
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
  updateDoc, // ✅ ADD
  deleteDoc, // ✅ ADD (for delete option)
} from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) => Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

export default function EngineerRaBillGenerator() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  // ✅ Role (CEO/CIO/Admin)
  const [role, setRole] = useState("");

  // projects dropdown
  const [tenders, setTenders] = useState([]);
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [selectedTenderId, setSelectedTenderId] = useState("");

  const [tender, setTender] = useState(null);
  const [contractor, setContractor] = useState(null);

  const [rows, setRows] = useState([]); // RA table rows
  const [raDate, setRaDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [saving, setSaving] = useState(false);
  const [existingRaBills, setExistingRaBills] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // ✅ Final RA Bills (merged)
  const [finalRaBills, setFinalRaBills] = useState([]);
  const [loadingFinal, setLoadingFinal] = useState(false);

  const isCEOUser = role === "CEO" || role === "CIO" || role === "Admin";

  /* ---------- AUTH (Engineer login) ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        toast.error("Please login as Engineer.");
        navigate("/tender/engineer", { replace: true });
        return;
      }
      setUser(u);

      // ✅ fetch role claims (CEO/CIO/Admin)
      try {
        const token = await u.getIdTokenResult(true);
        setRole(token?.claims?.role || "");
      } catch (e) {
        console.error(e);
        setRole("");
      }
    });
    return () => unsub();
  }, [navigate]);

  /* ---------- LOAD awarded tenders list ---------- */
  useEffect(() => {
    const loadTenders = async () => {
      if (!user) return;
      try {
        setLoadingTenders(true);
        const snap = await getDocs(collection(db, "tenders"));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        const awarded = list.filter((t) => t.awardedVendorUid);
        awarded.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );

        setTenders(awarded);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load projects for Engineer RA.");
      } finally {
        setLoadingTenders(false);
      }
    };
    loadTenders();
  }, [user]);

  /* ---------- LOAD FINAL RA BILLS for selected tender ---------- */
  useEffect(() => {
    const loadFinal = async () => {
      if (!selectedTenderId) {
        setFinalRaBills([]);
        return;
      }
      try {
        setLoadingFinal(true);
        const snap = await getDocs(
          query(
            collection(db, "ra_bills_final"),
            where("tenderId", "==", selectedTenderId)
          )
        );
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        // latest first
        list.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );

        setFinalRaBills(list);
      } catch (e) {
        console.error(e);
        setFinalRaBills([]);
      } finally {
        setLoadingFinal(false);
      }
    };
    loadFinal();
  }, [selectedTenderId]);

  /* ---------- LOAD RA + BOQ + ENGINEER MEASUREMENTS FOR SELECTED TENDER ---------- */
  useEffect(() => {
    const load = async () => {
      if (!user || !selectedTenderId) {
        setTender(null);
        setContractor(null);
        setRows([]);
        setExistingRaBills([]);
        return;
      }

      try {
        setLoadingData(true);

        // Tender
        const tSnap = await getDoc(doc(db, "tenders", selectedTenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          return;
        }
        const t = { id: tSnap.id, ...tSnap.data() };
        setTender(t);

        // Contractor (awarded vendor)
        if (t.awardedVendorUid) {
          const vSnap = await getDoc(doc(db, "vendors", t.awardedVendorUid));
          if (vSnap.exists()) setContractor({ id: vSnap.id, ...vSnap.data() });
          else setContractor(null);
        } else {
          setContractor(null);
        }

        // BOQ items
        const boqSnap = await getDocs(
          collection(db, "tenders", selectedTenderId, "boq_items")
        );
        const boqItems = [];
        boqSnap.forEach((d) => boqItems.push({ id: d.id, ...d.data() }));
        boqItems.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));

        // Previous Engineer RA bills
        const qRef = query(
          collection(db, "ra_bills_engineer"),
          where("tenderId", "==", selectedTenderId)
        );
        const raSnap = await getDocs(qRef);
        const raList = [];
        raSnap.forEach((d) => raList.push({ id: d.id, ...d.data() }));
        raList.sort(
          (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
        );
        setExistingRaBills(raList);

        // map: itemId -> total qty from all previous Engineer RA bills
        const prevRaQtyMap = {};
        raList.forEach((bill) => {
          (bill.items || []).forEach((it) => {
            const key = it.itemId || it.boqItemId;
            if (!key) return;
            prevRaQtyMap[key] =
              (prevRaQtyMap[key] || 0) + toNum(it.qtyThisRa || it.qty || 0);
          });
        });

        // ✅ Engineer measurements (multi-sheet sum)
        const measSnap = await getDocs(
          collection(db, "tenders", selectedTenderId, "measurements_engineer")
        );

        // key = boqId, value = total measured qty (sum of sheets)
        const measMap = {};

        measSnap.forEach((d) => {
          const data = d.data();
          const key = data.boqId;
          if (!key) return;

          // new system
          let sheetQty = toNum(data.sheetQty || 0);

          // fallback old docs
          if (!sheetQty && Array.isArray(data.rows)) {
            sheetQty = data.rows.reduce((s, r) => s + toNum(r.qty || 0), 0);
          }
          if (!sheetQty) sheetQty = toNum(data.engineerTotalQty || 0);

          measMap[key] = (measMap[key] || 0) + sheetQty;
        });

        // Build table rows
        const tableRows = boqItems.map((it) => {
          const boqQty = toNum(it.boqQty);
          const rate = toNum(it.rateWithLC || it.rate || 0);

          // engineer measured total (max BOQ)
          const measuredTotal = Math.min(toNum(measMap[it.id] || 0), boqQty);

          const executedPrevRa = toNum(prevRaQtyMap[it.id] || 0);

          // pending measured
          const pendingMeasured = Math.max(measuredTotal - executedPrevRa, 0);

          // qty this RA = pending measured (auto)
          const qtyThisRa = pendingMeasured;
          const amountThisRa = qtyThisRa * rate;

          return {
            id: it.id,
            itemNo: it.itemNo,
            description: it.description,
            unit: it.unit || it.boqUnit || "",
            boqQty,
            rate,
            executedMeasured: measuredTotal,
            executedPrevRa,
            pending: pendingMeasured,
            qtyThisRa,
            amountThisRa,
          };
        });

        setRows(tableRows);
      } catch (err) {
        console.error("ENGINEER RA LOAD ERROR:", err);
        toast.error(err?.message || "Failed to load Engineer RA bill.");
      } finally {
        setLoadingData(false);
      }
    };

    if (user) load();
  }, [user, selectedTenderId]);

  const raTotal = rows.reduce((sum, r) => sum + (r.amountThisRa || 0), 0);

  /* ---------- LOCK MEASUREMENTS after RA ---------- */
  const lockEngineerMeasurements = async (lockField, value) => {
    try {
      const measSnap = await getDocs(
        collection(db, "tenders", selectedTenderId, "measurements_engineer")
      );
      for (const d of measSnap.docs) {
        await updateDoc(d.ref, { [lockField]: value });
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* ---------- Generate new Engineer RA bill ---------- */
  const handleGenerateRa = async () => {
    if (!selectedTenderId) {
      toast.error("Select a project first.");
      return;
    }

    const itemsThisRa = rows.filter((r) => r.qtyThisRa > 0);
    if (!itemsThisRa.length) {
      toast.error("No measured quantity pending for Engineer RA.");
      return;
    }

    if (!raDate) {
      toast.error("Select Engineer RA bill date.");
      return;
    }

    try {
      setSaving(true);

      const nextIndex = existingRaBills.length + 1;
      const raNo = `ENG-RA-${String(nextIndex).padStart(2, "0")}`;

      const totalQtyThisRa = itemsThisRa.reduce(
        (sum, r) => sum + toNum(r.qtyThisRa || 0),
        0
      );

      const payload = {
        tenderId: selectedTenderId,
        tenderTitle: tender?.title || tender?.projectName || "",
        contractorName:
          contractor?.profile?.companyName ||
          contractor?.vendorId ||
          tender?.awardedVendorName ||
          "",
        raNo,
        raDate,
        totalQtyThisRa,
        totalAmount: raTotal,
        status: "pending",
        createdAt: serverTimestamp(),
        items: itemsThisRa.map((r) => ({
          itemId: r.id,
          itemNo: r.itemNo,
          description: r.description,
          unit: r.unit,
          boqQty: r.boqQty,
          rate: r.rate,
          executedPrev: r.executedPrevRa,
          qtyThisRa: r.qtyThisRa,
          executedTotal: r.executedPrevRa + r.qtyThisRa,
          amount: r.amountThisRa,
        })),
      };

      const docRef = await addDoc(collection(db, "ra_bills_engineer"), payload);

      toast.success(`Engineer RA ${raNo} generated.`);

      // 🔒 lock measurements after Engineer RA generation
      await lockEngineerMeasurements("lockedByRa", true);

      // update table rows
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          executedPrevRa: r.executedPrevRa + r.qtyThisRa,
          pending: 0,
          qtyThisRa: 0,
          amountThisRa: 0,
        }))
      );

      // add to list
      setExistingRaBills((prev) => [...prev, { ...payload, id: docRef.id }]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate Engineer RA.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- CEO APPROVE Engineer RA ---------- */
  const approveEngineerRa = async (ra) => {
    if (!isCEOUser) {
      toast.error("Only CEO/CIO/Admin can approve.");
      return;
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, "ra_bills_engineer", ra.id), {
        status: "approved",
        approvedBy: user?.email || "",
        approvedAt: serverTimestamp(),
      });
      toast.success(`Engineer RA ${ra.raNo} approved.`);
      setExistingRaBills((prev) =>
        prev.map((x) => (x.id === ra.id ? { ...x, status: "approved" } : x))
      );
    } catch (e) {
      console.error(e);
      toast.error("CEO approval failed.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- ✅ DELETE Engineer RA (Pending only) ---------- */
  const deleteEngineerRa = async (ra) => {
    if (!isCEOUser) {
      toast.error("Only CEO/CIO/Admin can delete.");
      return;
    }
    if ((ra.status || "pending") !== "pending") {
      toast.error("Approved / Final RA cannot be deleted.");
      return;
    }

    const ok = window.confirm(`Delete Engineer RA ${ra.raNo}?`);
    if (!ok) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, "ra_bills_engineer", ra.id));
      toast.success("Engineer RA deleted.");

      setExistingRaBills((prev) => prev.filter((x) => x.id !== ra.id));
    } catch (e) {
      console.error(e);
      toast.error("Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/tender/engineer", { replace: true });
  };

  const raTotalDisplay = raTotal.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

  /* ---------- RENDER ---------- */

  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="text-sm text-slate-600">Loading Engineer RA bill…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-emerald-50 pb-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* HEADER */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                Engineer RA Bills – Engineer
              </div>
              {tender ? (
                <>
                  <h1 className="text-xl md:text-2xl font-extrabold text-emerald-800">
                    {tender.title || tender.projectName}
                  </h1>
                  <div className="mt-1 text-[11px] text-slate-600">
                    {tender.ownerCompany || ""} · {tender.city || ""}
                  </div>
                  {contractor && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      Contractor:{" "}
                      <b>
                        {contractor?.profile?.companyName || contractor?.vendorId}
                      </b>
                    </div>
                  )}
                </>
              ) : (
                <h1 className="text-lg font-semibold text-slate-700">
                  Select Project for Engineer RA Bills
                </h1>
              )}
              <div className="mt-1 text-[10px] text-slate-500">
                Role: {role || "ENGINEER"} {isCEOUser ? "• Approval Access" : ""}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleLogout}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
              >
                Logout
              </button>
              <Link
                to="/tender/engineer"
                className="text-[11px] text-emerald-700 underline"
              >
                ← Back to Engineer Dashboard
              </Link>
            </div>
          </div>

          {/* PROJECT SELECTOR */}
          <div className="mb-4 bg-white border border-emerald-100 rounded-xl p-3 text-[11px]">
            <div className="text-slate-600 mb-1">Select Project / Tender</div>

            {loadingTenders ? (
              <div className="text-[11px] text-slate-500">Loading projects…</div>
            ) : tenders.length === 0 ? (
              <div className="text-[11px] text-slate-500">No awarded tenders found.</div>
            ) : (
              <select
                value={selectedTenderId}
                onChange={(e) => setSelectedTenderId(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-[11px]"
              >
                <option value="">— Select Project —</option>
                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.title || t.projectName || "Untitled") +
                      " · " +
                      (t.city || t.district || "") +
                      " · [" +
                      t.id +
                      "]"}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedTenderId && loadingData && (
            <div className="text-[12px] text-slate-500 mb-4">Loading Engineer RA data…</div>
          )}

          {!selectedTenderId && (
            <div className="text-[12px] text-slate-600">
              Please select a project from above to view Engineer RA Bills.
            </div>
          )}

          {selectedTenderId && !loadingData && (
            <>
              {/* CONTROLS */}
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px]">
                  <div className="text-slate-500">Engineer RA Date</div>
                  <input
                    type="date"
                    value={raDate}
                    onChange={(e) => setRaDate(e.target.value)}
                    className="mt-1 px-2 py-1 border rounded-md text-[11px]"
                  />
                </div>

                <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px]">
                  <div className="text-slate-500">Previous Engineer RAs</div>
                  <div className="font-semibold text-emerald-700">
                    {existingRaBills.length} generated
                  </div>
                </div>

                <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px] ml-auto">
                  <div className="text-slate-500">Engineer RA Amount (This Bill)</div>
                  <div className="font-bold text-emerald-800 text-sm">₹ {raTotalDisplay}</div>
                </div>
              </div>

              {/* MAIN TABLE */}
              <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-3 overflow-auto">
                {rows.length === 0 ? (
                  <div className="text-[12px] text-slate-500">
                    BOQ items or engineer measurements not available.
                  </div>
                ) : (
                  <table className="min-w-full text-[11px] border border-slate-200">
                    <thead className="bg-emerald-50">
                      <tr>
                        <th className="border border-slate-200 px-2 py-1">Item</th>
                        <th className="border border-slate-200 px-2 py-1 text-left">Description</th>
                        <th className="border border-slate-200 px-2 py-1">Unit</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">BOQ Qty</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">Measured (Engineer)</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">Pending (measured)</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">Rate</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">Qty in this RA</th>
                        <th className="border border-slate-200 px-2 py-1 text-right">Amount (This RA)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="odd:bg-white even:bg-emerald-50/20">
                          <td className="border border-slate-200 px-2 py-1">{r.itemNo}</td>
                          <td className="border border-slate-200 px-2 py-1 text-left">{r.description}</td>
                          <td className="border border-slate-200 px-2 py-1 text-center">{r.unit}</td>
                          <td className="border border-slate-200 px-2 py-1 text-right">{r.boqQty.toFixed(3)}</td>
                          <td className="border border-slate-200 px-2 py-1 text-right">{r.executedMeasured.toFixed(3)}</td>
                          <td className="border border-slate-200 px-2 py-1 text-right text-emerald-700 font-semibold">
                            {r.pending.toFixed(3)}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">{r.rate.toFixed(2)}</td>
                          <td className="border border-slate-200 px-2 py-1 text-right">{r.qtyThisRa.toFixed(3)}</td>
                          <td className="border border-slate-200 px-2 py-1 text-right">{r.amountThisRa.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-emerald-100 font-bold">
                        <td colSpan={8} className="border border-slate-200 px-2 py-1 text-right">
                          TOTAL
                        </td>
                        <td className="border border-slate-200 px-2 py-1 text-right">
                          ₹ {raTotalDisplay}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* Generate Button */}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleGenerateRa}
                  disabled={saving || !rows.length}
                  className={cx(
                    "px-4 py-2 rounded-lg text-xs font-semibold text-white",
                    saving || !rows.length
                      ? "bg-emerald-300 cursor-not-allowed"
                      : "bg-emerald-700 hover:bg-emerald-800"
                  )}
                >
                  {saving ? "Generating Engineer RA…" : "Generate Engineer RA"}
                </button>
              </div>

              {/* ENGINEER RA LIST */}
              <div className="mt-6 border border-emerald-200 rounded-2xl bg-white shadow-sm overflow-hidden">
                <div className="px-3 py-2 bg-slate-900 text-[10px] text-white font-semibold flex justify-between">
                  <span>Engineer RA Bills (All for this project)</span>
                </div>

                <table className="min-w-full text-[10px] border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1">Sr</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">RA No</th>
                      <th className="border border-slate-300 px-2 py-1">Date</th>
                      <th className="border border-slate-300 px-2 py-1 text-right">Amount</th>
                      <th className="border border-slate-300 px-2 py-1">Status</th>
                      <th className="border border-slate-300 px-2 py-1">Download</th>
                      <th className="border border-slate-300 px-2 py-1">CEO</th>
                      <th className="border border-slate-300 px-2 py-1">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingRaBills.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="border border-slate-300 px-2 py-2 text-center text-slate-500">
                          No Engineer RA bills generated yet.
                        </td>
                      </tr>
                    ) : (
                      existingRaBills
                        .slice()
                        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
                        .map((b, idx) => (
                          <tr key={b.id} className="odd:bg-white even:bg-slate-50">
                            <td className="border border-slate-300 px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-slate-300 px-2 py-1">{b.raNo || "—"}</td>
                            <td className="border border-slate-300 px-2 py-1 text-center">{b.raDate || "—"}</td>
                            <td className="border border-slate-300 px-2 py-1 text-right">
                              ₹ {(b.totalAmount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </td>
                            <td className="border border-slate-300 px-2 py-1 text-center">
                              <span
                                className={cx(
                                  "px-2 py-0.5 rounded-full text-[9px] font-semibold",
                                  b.status === "approved"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-amber-100 text-amber-700"
                                )}
                              >
                                {b.status || "pending"}
                              </span>
                            </td>
                            <td className="border border-slate-300 px-2 py-1 text-center">
                              <button
                                type="button"
                                onClick={() => generateEngineerRaPdf(tender, b)}
                                className="px-2 py-0.5 rounded bg-slate-900 text-white"
                              >
                                PDF
                              </button>
                            </td>

                            <td className="border border-slate-300 px-2 py-1 text-center">
                              {isCEOUser && (b.status || "pending") === "pending" ? (
                                <button
                                  type="button"
                                  onClick={() => approveEngineerRa(b)}
                                  disabled={saving}
                                  className="px-2 py-0.5 rounded bg-emerald-700 text-white"
                                >
                                  Approve
                                </button>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>

                            <td className="border border-slate-300 px-2 py-1 text-center">
                              {isCEOUser && (b.status || "pending") === "pending" ? (
                                <button
                                  type="button"
                                  onClick={() => deleteEngineerRa(b)}
                                  disabled={saving}
                                  className="px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- Engineer RA PDF (kept same) ---------- */
function generateEngineerRaPdf(tender, bill) {
  try {
    const docp = new jsPDF("p", "mm", "a4");
    const pageWidth = docp.internal.pageSize.getWidth();
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;

    const title =
      tender?.title || tender?.projectName || "Engineer Running Account Bill";
    const pacsName =
      tender?.ownerCompany || tender?.pacsName || tender?.pacsShortName || "";

    const raNo = bill.raNo || "";
    const raDate =
      bill.raDate ||
      (bill.createdAt?.toDate
        ? dayjs(bill.createdAt.toDate()).format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY"));

    const contractorName = bill.contractorName || "";

    docp.setFont("helvetica", "bold");
    docp.setFontSize(14);
    docp.text("Engineer Running Account Bill", pageWidth / 2, 15, {
      align: "center",
    });

    docp.setFont("helvetica", "normal");
    docp.setFontSize(9);

    const titleLines = docp.splitTextToSize(title, contentWidth);
    docp.text(titleLines, marginLeft, 22);
    let y = 22 + titleLines.length * 4;

    if (pacsName) {
      docp.text(`PACS: ${pacsName}`, marginLeft, y);
      y += 4;
    }

    docp.text(`RA No: ${raNo}`, marginLeft, y);
    docp.text(`Date: ${raDate}`, pageWidth - marginRight, y, {
      align: "right",
    });
    y += 4;

    if (contractorName) {
      docp.text(`Contractor: ${contractorName}`, marginLeft, y);
      y += 5;
    }

    const body = (bill.items || []).map((it, idx) => [
      idx + 1,
      it.description || "",
      it.unit || "",
      (it.boqQty || 0).toFixed(3),
      (it.executedPrev || 0).toFixed(3),
      (it.qtyThisRa || 0).toFixed(3),
      (it.rate || 0).toFixed(2),
      (it.amount || 0).toFixed(2),
    ]);

    autoTable(docp, {
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      head: [
        [
          "Sr",
          "Description",
          "Unit",
          "BOQ Qty",
          "Prev Qty",
          "This RA Qty",
          "Rate",
          "Amount (This RA)",
        ],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineWidth: 0.25,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 10, halign: "center" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
        5: { cellWidth: 18, halign: "right" },
        6: { cellWidth: 16, halign: "right" },
        7: { cellWidth: 22, halign: "right" },
      },
    });

    const tableEndY = docp.lastAutoTable.finalY || y + 40;
    const total = bill.totalAmount || 0;

    docp.setFont("helvetica", "bold");
    docp.setFontSize(10);
    docp.text(
      `TOTAL (This Engineer RA): Rs. ${Number(total || 0).toFixed(2)}`,
      pageWidth - marginRight,
      tableEndY + 8,
      { align: "right" }
    );

    const sigY = tableEndY + 24;
    const sigWidth = (contentWidth - 20) / 2;
    const x1 = marginLeft;
    const x2 = marginLeft + sigWidth + 10;

    docp.setLineWidth(0.4);
    docp.line(x1, sigY, x1 + sigWidth, sigY);
    docp.line(x2, sigY, x2 + sigWidth, sigY);

    docp.setFontSize(8);
    docp.text("PMC Engineer", x1 + sigWidth / 2, sigY + 4, {
      align: "center",
    });
    docp.text("PMC CEO", x2 + sigWidth / 2, sigY + 4, {
      align: "center",
    });

    docp.save(`${raNo || "Engineer_RA"}.pdf`);
  } catch (e) {
    console.error(e);
    toast.error("Engineer RA PDF generation failed.");
  }
}
